import { clamp } from './helpers'
import {
  dilateMask,
  mergeMasksMax,
  createLayerFromAlpha,
  buildBorderBackgroundAlpha,
  countOpaque,
  buildPolygonAlphaMask
} from './imageUtils'
import { FACE_MESH_CONTOUR_PATHS } from '../avatarInspector/featureIndices'

const HAIR_CONF_MIN = 0.33
const HAIR_CONF_MAX = 0.85
const HAIR_ALPHA_MAX = 210 // Match inspector softness
const HAIR_ALPHA_BASE = 110
const HAIR_ALPHA_RANGE = 110
const HAIR_CATEGORY_ALPHA = 185

const segmentHair = (segmenter, img) => {
  if (!segmenter || !img) return null
  try {
    if (typeof segmenter.segment === 'function') return segmenter.segment(img)
    if (typeof segmenter.segmentImage === 'function') return segmenter.segmentImage(img, 'hair')
  } catch {
    return null
  }
  return null
}

export const buildHeuristicHairAlpha = (width, height, rig) => {
  const alpha = new Uint8ClampedArray(width * height)
  if (!rig?.faceMetrics) return alpha
  const cx = rig.faceMetrics.centerX
  const cy = rig.faceMetrics.centerY - rig.faceMetrics.faceH * 0.28
  const rx = rig.faceMetrics.faceW * 0.9
  const ry = rig.faceMetrics.faceH * 1.25
  const cutoffY = rig.faceMetrics.centerY + rig.faceMetrics.faceH * 0.2
  for (let y = 0; y < height; y += 1) {
    if (y > cutoffY) continue
    for (let x = 0; x < width; x += 1) {
      const nx = (x - cx) / rx
      const ny = (y - cy) / ry
      if (nx * nx + ny * ny <= 1) {
        alpha[y * width + x] = 255
      }
    }
  }
  return alpha
}

export const splitHairAlpha = (hairAlpha, width, height, rig) => {
  const front = new Uint8ClampedArray(width * height)
  const back = new Uint8ClampedArray(width * height)
  if (!hairAlpha?.length || !rig?.faceMetrics) return { front, back }

  const cx = rig.faceMetrics.centerX
  const cy = rig.faceMetrics.centerY
  const faceW = rig.faceMetrics.faceW
  const faceH = rig.faceMetrics.faceH
  let frontCount = 0
  let hairCount = 0
  const faceOval = FACE_MESH_CONTOUR_PATHS?.faceOval || []
  let faceInner = null
  let faceContact = null
  let faceContactWide = null
  if (rig?.pointsPx?.length && faceOval?.length) {
    try {
      const faceAlpha = buildPolygonAlphaMask(width, height, rig.pointsPx, [faceOval])
      if (countOpaque(faceAlpha) > 0) {
        faceInner = dilateMask(faceAlpha, width, height, 2)
        const contactRadius = Math.max(6, Math.min(28, Math.round(faceW * 0.12)))
        const contactWideRadius = Math.max(10, Math.min(36, Math.round(faceW * 0.18)))
        faceContact = dilateMask(faceAlpha, width, height, contactRadius)
        faceContactWide = dilateMask(faceAlpha, width, height, contactWideRadius)
      }
    } catch {
      faceInner = null
      faceContact = null
      faceContactWide = null
    }
  }

  if (faceInner && faceContact && faceContactWide) {
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const idx = y * width + x
        const a = hairAlpha[idx]
        if (!a) continue
        hairCount += 1
        const dx = Math.abs(x - cx)
        const inInner = faceInner[idx] > 0
        const inContact = faceContact[idx] > 0
        const inContactWide = faceContactWide[idx] > 0
        const foreheadBand = y <= cy + faceH * 0.18 && y >= cy - faceH * 0.8 && dx <= faceW * 0.98
        const foreheadCore = y <= cy + faceH * 0.12 && y >= cy - faceH * 0.66 && dx <= faceW * 0.74
        const templeBand =
          y <= cy + faceH * 0.14 &&
          y >= cy - faceH * 0.54 &&
          dx >= faceW * 0.24 &&
          dx <= faceW * 1.08
        const isFront =
          !inInner &&
          ((inContact && foreheadBand) || (inContactWide && (foreheadCore || templeBand)))
        if (isFront) {
          front[idx] = a
          frontCount += 1
        } else {
          back[idx] = a
        }
      }
    }

    // If the front band is too narrow, relax the contact band to recover a connected forehead strip.
    if (hairCount > 0 && frontCount < Math.max(56, Math.floor(hairCount * 0.015))) {
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const idx = y * width + x
          const a = hairAlpha[idx]
          if (!a || front[idx] > 0) continue
          const dx = Math.abs(x - cx)
          const inInner = faceInner[idx] > 0
          const inContactWide = faceContactWide[idx] > 0
          const upperBand = y <= cy + faceH * 0.2 && y >= cy - faceH * 0.76 && dx <= faceW * 1.02
          if (inContactWide && !inInner && upperBand) {
            front[idx] = a
            back[idx] = 0
            frontCount += 1
          }
        }
      }
    }

    // If front grabs too much, clamp to a strict upper forehead/temple strip.
    if (hairCount > 0 && frontCount > Math.floor(hairCount * 0.45)) {
      front.fill(0)
      frontCount = 0
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const idx = y * width + x
          const a = hairAlpha[idx]
          if (!a) continue
          const dx = Math.abs(x - cx)
          const inInner = faceInner[idx] > 0
          const inContact = faceContact[idx] > 0
          const upperBand = y <= cy + faceH * 0.14 && y >= cy - faceH * 0.74 && dx <= faceW * 0.92
          const templeBand =
            y <= cy + faceH * 0.12 &&
            y >= cy - faceH * 0.52 &&
            dx >= faceW * 0.26 &&
            dx <= faceW * 1.02
          if (!inInner && inContact && (upperBand || templeBand)) {
            front[idx] = a
            frontCount += 1
          }
        }
      }
    }
  } else {
    // Fallback when face-oval mask cannot be built.
    const ezx = Math.max(1, faceW * 0.68)
    const ezy = Math.max(1, faceH * 0.8)
    const ex = cx
    const ey = cy - faceH * 0.24
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const idx = y * width + x
        const a = hairAlpha[idx]
        if (!a) continue
        hairCount += 1

        const nx = (x - ex) / ezx
        const ny = (y - ey) / ezy
        const inForeheadEllipse = nx * nx + ny * ny <= 1.08
        const inTopBand = y <= cy + faceH * 0.18
        const inTempleBand =
          y <= cy + faceH * 0.08 &&
          Math.abs(x - cx) <= faceW * 0.92 &&
          Math.abs(x - cx) >= faceW * 0.28
        const isFront = (inTopBand && inForeheadEllipse) || inTempleBand

        if (isFront) {
          front[idx] = a
          frontCount += 1
        } else {
          back[idx] = a
        }
      }
    }
  }

  // Avoid accidentally classifying almost everything as front.
  if (hairCount && frontCount > hairCount * 0.6) {
    front.fill(0)
    for (let i = 0; i < hairAlpha.length; i += 1) {
      back[i] = hairAlpha[i]
    }
  }

  const frontDilated = dilateMask(front, width, height, 1)
  const frontOut = new Uint8ClampedArray(width * height)
  for (let p = 0; p < width * height; p += 1) {
    if ((hairAlpha[p] || 0) <= 0) continue
    if (front[p] > 0) {
      frontOut[p] = front[p]
      continue
    }
    if (frontDilated[p] > 0) {
      frontOut[p] = Math.min(hairAlpha[p], Math.max(1, Math.round(frontDilated[p] * 0.92)))
    }
  }
  const backOut = new Uint8ClampedArray(width * height)
  for (let p = 0; p < width * height; p += 1) {
    backOut[p] = frontOut[p] > 0 ? 0 : hairAlpha[p]
  }

  return {
    front: frontOut,
    back: backOut
  }
}

export const computeHairAlpha = (segmenter, img, width, height, rig, options = {}) => {
  const hairAlpha = new Uint8ClampedArray(width * height)
  let source = 'mesh-face'
  let hairFilled = false
  const maskSampling =
    options?.maskSampling === 'nearest' || options?.sampling === 'nearest' ? 'nearest' : 'bilinear'
  const useNearest = maskSampling === 'nearest'
  if (segmenter && img) {
    try {
      const result = segmentHair(segmenter, img)
      const confidenceMasks = result?.confidenceMasks
      if (confidenceMasks?.length) {
        const hairMask = confidenceMasks[1] || confidenceMasks[0]
        const floats = hairMask?.getAsFloat32Array?.()
        const maskWidth = hairMask?.width || 0
        const maskHeight = hairMask?.height || 0
        if (floats?.length && maskWidth * maskHeight === floats.length) {
          // Bilinear upsample is default for smoother edges.
          const sx = (maskWidth - 1) / Math.max(1, width - 1)
          const sy = (maskHeight - 1) / Math.max(1, height - 1)
          for (let y = 0; y < height; y += 1) {
            const fy = y * sy
            const y0 = clamp(Math.floor(fy), 0, maskHeight - 1)
            const y1 = clamp(y0 + 1, 0, maskHeight - 1)
            const ty = fy - y0
            const row0 = y0 * maskWidth
            const row1 = y1 * maskWidth
            for (let x = 0; x < width; x += 1) {
              const fx = x * sx
              let conf = 0
              if (useNearest) {
                const mx = clamp(Math.round(fx), 0, maskWidth - 1)
                const my = clamp(Math.round(fy), 0, maskHeight - 1)
                conf = clamp(floats[my * maskWidth + mx] || 0, 0, 1)
              } else {
                const x0 = clamp(Math.floor(fx), 0, maskWidth - 1)
                const x1 = clamp(x0 + 1, 0, maskWidth - 1)
                const tx = fx - x0
                const i00 = floats[row0 + x0] || 0
                const i10 = floats[row0 + x1] || 0
                const i01 = floats[row1 + x0] || 0
                const i11 = floats[row1 + x1] || 0
                const i0 = i00 + (i10 - i00) * tx
                const i1 = i01 + (i11 - i01) * tx
                conf = clamp(i0 + (i1 - i0) * ty, 0, 1)
              }
              hairAlpha[y * width + x] =
                conf < HAIR_CONF_MIN
                  ? 0
                  : conf >= HAIR_CONF_MAX
                    ? HAIR_ALPHA_MAX
                    : Math.floor(HAIR_ALPHA_BASE + conf * HAIR_ALPHA_RANGE)
            }
          }
          hairFilled = true
        }
      }
      if (!hairFilled) {
        const mask = result?.categoryMask?.getAsImageData?.()
        if (mask?.data?.length) {
          const maskWidth = mask.width || 0
          const maskHeight = mask.height || 0
          if (maskWidth > 0 && maskHeight > 0) {
            const sx = (maskWidth - 1) / Math.max(1, width - 1)
            const sy = (maskHeight - 1) / Math.max(1, height - 1)
            const data = mask.data
            for (let y = 0; y < height; y += 1) {
              const fy = y * sy
              const y0 = clamp(Math.floor(fy), 0, maskHeight - 1)
              const y1 = clamp(y0 + 1, 0, maskHeight - 1)
              const ty = fy - y0
              const row0 = y0 * maskWidth
              const row1 = y1 * maskWidth
              for (let x = 0; x < width; x += 1) {
                const fx = x * sx
                let conf = 0
                if (useNearest) {
                  const mx = clamp(Math.round(fx), 0, maskWidth - 1)
                  const my = clamp(Math.round(fy), 0, maskHeight - 1)
                  conf = clamp((data[(my * maskWidth + mx) * 4] || 0) / 255, 0, 1)
                } else {
                  const x0 = clamp(Math.floor(fx), 0, maskWidth - 1)
                  const x1 = clamp(x0 + 1, 0, maskWidth - 1)
                  const tx = fx - x0
                  const i00 = (data[(row0 + x0) * 4] || 0) / 255
                  const i10 = (data[(row0 + x1) * 4] || 0) / 255
                  const i01 = (data[(row1 + x0) * 4] || 0) / 255
                  const i11 = (data[(row1 + x1) * 4] || 0) / 255
                  const i0 = i00 + (i10 - i00) * tx
                  const i1 = i01 + (i11 - i01) * tx
                  conf = clamp(i0 + (i1 - i0) * ty, 0, 1)
                }
                hairAlpha[y * width + x] = Math.round(conf * HAIR_CATEGORY_ALPHA)
              }
            }
            hairFilled = true
          }
        }
      }
    } catch {
      /* ignore */
    }
  }

  // Try heuristic only if allowed
  if (!countOpaque(hairAlpha)) {
    if (options?.disableHairHeuristic !== true) {
      const heuristic = buildHeuristicHairAlpha(width, height, rig)
      for (let p = 0; p < width * height; p += 1)
        hairAlpha[p] = Math.max(hairAlpha[p], heuristic[p])
      if (countOpaque(hairAlpha) > 0)
        source = segmenter ? 'mesh-face+hair-seg' : 'mesh-face+hair-heuristic'
    } else {
      source = 'mesh-face' // no hair
    }
  } else {
    source = 'mesh-face+hair-seg'
  }

  return { hairAlpha, source }
}

const pickHairStep = (width, height, options = {}) => {
  const base = Math.max(1, Math.min(width, height))
  const suggested = Math.round(base / 38)
  const minStep = Number.isFinite(options.minStep) ? options.minStep : 6
  const maxStep = Number.isFinite(options.maxStep) ? options.maxStep : 16
  return Math.max(minStep, Math.min(maxStep, suggested || 8))
}

export const buildHairMeshFromAlpha = (alpha, width, height, options = {}) => {
  if (!alpha?.length || !width || !height) return null
  const threshold = Number.isFinite(options.threshold) ? options.threshold : 16
  const step = Number.isFinite(options.step)
    ? Math.max(2, Math.round(options.step))
    : pickHairStep(width, height, options)
  const cols = Math.max(1, Math.ceil((width - 1) / step))
  const rows = Math.max(1, Math.ceil((height - 1) / step))
  const points = new Array((cols + 1) * (rows + 1))
  for (let gy = 0; gy <= rows; gy += 1) {
    const y = Math.min(height - 1, gy * step)
    for (let gx = 0; gx <= cols; gx += 1) {
      const x = Math.min(width - 1, gx * step)
      points[gy * (cols + 1) + gx] = { x, y }
    }
  }

  const triangles = []
  const sampleAlpha = (x, y) => {
    const ix = clamp(Math.round(x), 0, width - 1)
    const iy = clamp(Math.round(y), 0, height - 1)
    return alpha[iy * width + ix] || 0
  }
  for (let gy = 0; gy < rows; gy += 1) {
    for (let gx = 0; gx < cols; gx += 1) {
      const x0 = gx * step
      const y0 = gy * step
      const x1 = Math.min(width - 1, x0 + step)
      const y1 = Math.min(height - 1, y0 + step)
      const a00 = sampleAlpha(x0, y0)
      const a10 = sampleAlpha(x1, y0)
      const a01 = sampleAlpha(x0, y1)
      const a11 = sampleAlpha(x1, y1)
      if (a00 <= threshold && a10 <= threshold && a01 <= threshold && a11 <= threshold) continue

      const idx = gy * (cols + 1) + gx
      const idxR = idx + 1
      const idxB = idx + (cols + 1)
      const idxBR = idxB + 1
      triangles.push(idx, idxR, idxB)
      triangles.push(idxB, idxR, idxBR)
    }
  }

  if (!triangles.length) return null
  return { points, triangles, step }
}

export const computeHairMeshDepth = (points, rig, options = {}) => {
  const depth = new Float32Array(points?.length || 0)
  if (!points?.length || !rig?.pointsPx?.length || !rig?.depthBias?.length) return depth
  const facePts = rig.pointsPx
  const faceDepth = rig.depthBias
  const faceW = rig.faceMetrics?.faceW || 120
  const maxDist = Math.max(20, faceW * 0.9)
  const falloff = Number.isFinite(options.falloff) ? options.falloff : 1

  for (let i = 0; i < points.length; i += 1) {
    const p = points[i]
    if (!p) continue
    let best = -1
    let bestD2 = Infinity
    for (let j = 0; j < facePts.length; j += 1) {
      const fp = facePts[j]
      if (!fp) continue
      const dx = fp.x - p.x
      const dy = fp.y - p.y
      const d2 = dx * dx + dy * dy
      if (d2 < bestD2) {
        bestD2 = d2
        best = j
      }
    }
    if (best >= 0) {
      const dist = Math.sqrt(bestD2)
      const w = Math.max(0, Math.min(1, 1 - dist / maxDist))
      depth[i] = (faceDepth[best] || 0) * Math.pow(w, falloff)
    }
  }
  return depth
}

export const buildPersonAvatarCutout = (avatarImage, segmenterImage, width, height, settings) => {
  if (!avatarImage?.img || !segmenterImage) return null
  try {
    const seg = segmenterImage.segment(avatarImage.img)
    let alpha = new Uint8ClampedArray(width * height)
    let filled = false
    const tune = (settings && settings.segmentationTuning) || {}
    const clamp01 = (v) => Math.max(0, Math.min(1, Number(v) || 0))
    const prob = clamp01(tune.probThreshold ?? 0.5)
    const cutIn = clamp01(prob) // full foreground above this
    const cutOut = clamp01(Math.max(0, prob - 0.25)) // fully background below this
    const confidenceMasks = seg?.confidenceMasks
    if (confidenceMasks?.length) {
      const personMask = confidenceMasks[1] || confidenceMasks[0]
      const floats = personMask?.getAsFloat32Array?.()
      const mw = personMask?.width || 0
      const mh = personMask?.height || 0
      if (floats?.length && mw * mh === floats.length) {
        const sx = mw / width
        const sy = mh / height
        for (let y = 0; y < height; y += 1) {
          const my = clamp(Math.floor(y * sy), 0, mh - 1)
          for (let x = 0; x < width; x += 1) {
            const mx = clamp(Math.floor(x * sx), 0, mw - 1)
            const conf = clamp(floats[my * mw + mx] || 0, 0, 1)
            alpha[y * width + x] =
              conf >= cutIn
                ? 255
                : conf <= cutOut
                  ? 0
                  : Math.round(((conf - cutOut) / Math.max(1e-6, cutIn - cutOut)) * 255)
          }
        }
        filled = true
      }
    }
    if (!filled) {
      const categoryMask = seg?.categoryMask?.getAsImageData?.()
      if (categoryMask?.data?.length) {
        const mw = categoryMask.width
        const mh = categoryMask.height
        const sx = mw / width
        const sy = mh / height
        for (let y = 0; y < height; y += 1) {
          const my = clamp(Math.floor(y * sy), 0, mh - 1)
          for (let x = 0; x < width; x += 1) {
            const mx = clamp(Math.floor(x * sx), 0, mw - 1)
            const raw = categoryMask.data[(my * mw + mx) * 4] || 0
            alpha[y * width + x] = raw > 0 ? 255 : 0
          }
        }
        filled = true
      }
    }
    if (!filled) return null

    // Combine with border BG for robustness
    const bgAlpha = buildBorderBackgroundAlpha(avatarImage.img, width, height)
    const subjectFromBorder = new Uint8ClampedArray(width * height)
    for (let p = 0; p < width * height; p += 1) subjectFromBorder[p] = 255 - (bgAlpha[p] || 0)
    let merged = mergeMasksMax(alpha, subjectFromBorder)
    // Apply optional foreground dilate/erode from tuning
    const extraDilate = Math.round(Number(tune.foregroundDilate) || 0)
    if (extraDilate !== 0) {
      if (extraDilate > 0) {
        merged = dilateMask(merged, width, height, extraDilate)
      } else {
        // Erode by dilating the inverse
        const inv = new Uint8ClampedArray(merged.length)
        for (let i = 0; i < merged.length; i += 1) inv[i] = 255 - (merged[i] || 0)
        const erodedInv = dilateMask(inv, width, height, Math.abs(extraDilate))
        for (let i = 0; i < merged.length; i += 1) merged[i] = 255 - (erodedInv[i] || 0)
      }
    }
    // Edge feather combines renderer featherPx and inspector feather
    const baseFeather = Math.max(0, Math.min(4, Math.round(Number(settings.featherPx) || 0)))
    const tuneFeather = Math.max(0, Math.round(Number(tune.edgeFeather) || 0))
    const softenRadius = Math.max(baseFeather, tuneFeather)
    const softened = softenRadius > 0 ? dilateMask(merged, width, height, softenRadius) : merged
    const canvas = createLayerFromAlpha(avatarImage.img, width, height, softened)
    return canvas
  } catch {
    return null
  }
}

export default {
  buildHeuristicHairAlpha,
  splitHairAlpha,
  computeHairAlpha,
  buildHairMeshFromAlpha,
  computeHairMeshDepth,
  buildPersonAvatarCutout
}
